/* PHANSTA first-party customer journey tracker  (phn-journey.js)
 * Why: Shopify only stores the referrer of the CONVERTING session
 * (order.landing_site / referring_site). Anything before that — the pin that
 * started it, an earlier organic visit, a stripped app-browser referrer — is
 * lost. This script keeps a 400-day first-party record in localStorage/cookie
 * and copies it onto the cart, so every ORDER carries its full journey in
 * note_attributes (readable via Admin API).
 *
 * Written to cart attributes: phn_first, phn_last, phn_trail, phn_sid, phn_visits.
 * Rollback: remove the script tag from layout/theme.liquid (+ theme.pagefly.liquid).
 */
(function () {
  'use strict';
  if (window.__phnLoaded) return;
  window.__phnLoaded = true;

  var KEY = 'phn_journey_v1';
  var UID_KEY = 'phn_uid_v1';
  var COOKIE = 'phn_id';
  var TTL_MS = 400 * 24 * 3600 * 1000;   // 400 days
  var SESSION_MS = 30 * 60 * 1000;       // 30 min session window
  var MAX_VISITS = 40;

  function uuid() {
    try {
      if (crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function readLS() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.created || (Date.now() - o.created) > TTL_MS) return null;
      return o;
    } catch (e) { return null; }
  }
  function writeLS(o) {
    try { localStorage.setItem(KEY, JSON.stringify(o)); } catch (e) {}
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function writeCookie(name, val, days) {
    try {
      var d = new Date(Date.now() + days * 86400000);
      document.cookie = name + '=' + encodeURIComponent(val) +
        '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function params() {
    var out = {};
    try {
      var q = new URLSearchParams(location.search);
      q.forEach(function (v, k) { out[k] = v; });
    } catch (e) {}
    return out;
  }

  function host(u) {
    if (!u) return '';
    try { return new URL(u, location.origin).hostname.replace(/^www\./, ''); }
    catch (e) { return ''; }
  }

  var SEARCH = /google|bing|duckduckgo|yahoo|brave|yandex|ecosia|baidu|startpage|search/i;
  var SOCIAL = /pinterest|facebook|instagram|tiktok|twitter|x\.com|reddit|linkedin|youtube|snapchat|threads/i;

  /* Canonical display names so reports never split the same channel into buckets. */
  var SOURCE_NAMES = [
    [/^pin(t(erest)?)?$/i, 'Pinterest'],
    [/^google(ads)?$/i, 'Google'],
    [/^instagram$/i, 'Instagram'],
    [/^facebook|^fb$/i, 'Facebook'],
    [/^tiktok$/i, 'TikTok'],
    [/^bing$/i, 'Bing'],
    [/^duckduckgo$/i, 'DuckDuckGo'],
    [/^brave$/i, 'Brave Search'],
    [/^yahoo$/i, 'Yahoo']
  ];
  function canonSource(s) {
    if (!s) return s;
    for (var i = 0; i < SOURCE_NAMES.length; i++) if (SOURCE_NAMES[i][0].test(s)) return SOURCE_NAMES[i][1];
    return s;
  }
  function hostSource(h) {
    var s = String(h || '').replace(/^www\./, '');
    if (/^android-app:/.test(s) || /pinterest/.test(s)) return 'Pinterest';
    if (/instagram/.test(s)) return 'Instagram';
    if (/facebook|^fb\./.test(s)) return 'Facebook';
    if (/tiktok/.test(s)) return 'TikTok';
    if (/reddit/.test(s)) return 'Reddit';
    if (/linkedin/.test(s)) return 'LinkedIn';
    if (/youtube/.test(s)) return 'YouTube';
    if (/(^|\.)x\.com$|twitter/.test(s)) return 'X';
    if (/^google\./.test(s)) return 'Google';
    if (/^bing\.|bing\.com/.test(s)) return 'Bing';
    if (/duckduckgo/.test(s)) return 'DuckDuckGo';
    if (/brave/.test(s)) return 'Brave Search';
    if (/yahoo/.test(s)) return 'Yahoo';
    if (/yandex/.test(s)) return 'Yandex';
    if (/ecosia/.test(s)) return 'Ecosia';
    if (/loox\.io/.test(s)) return 'Loox';
    return s.split('.')[0];
  }

  /* Classify the visit: explicit UTM wins, then platform signatures, then referrer, then direct. */
  function classify(ref, p) {
    var src = p.utm_source || p.pin_media_source || '';
    var med = p.utm_medium || '';
    if (src) {
      return { source: canonSource(src), medium: med || 'unknown', campaign: p.utm_campaign || '', content: p.utm_content || '' };
    }
    // Pinterest pin signatures survive a stripped referrer (in-app browser / privacy)
    var viewVal = '';
    try { viewVal = p.view ? decodeURIComponent(p.view) : ''; } catch (e) { viewVal = p.view || ''; }
    if (p.epik || p.pins_campaign_id || p.pp || /src=pin|pin_/i.test(viewVal)) {
      return { source: 'Pinterest', medium: 'paid', campaign: p.pins_campaign_id || '', content: '' };
    }
    // Google shopping/organic product listings tag
    if (p.srsltid) return { source: 'Google', medium: 'organic', campaign: '', content: '' };
    if (p.gclid) return { source: 'Google', medium: 'cpc', campaign: '', content: '' };
    if (!ref || ref === location.origin) return { source: '(direct)', medium: '(none)', campaign: '', content: '' };
    var h = host(ref);
    if (/^android-app:/.test(ref)) return { source: 'Pinterest', medium: 'referral', campaign: '', content: '' };
    if (SOCIAL.test(h)) return { source: hostSource(h), medium: 'referral', campaign: '', content: '' };
    if (SEARCH.test(h)) return { source: hostSource(h), medium: 'organic', campaign: '', content: '' };
    if (h && h !== host(location.href)) return { source: h, medium: 'referral', campaign: '', content: '' };
    return { source: '(direct)', medium: '(none)', campaign: '', content: '' };
  }

  var p = params();
  var ref = document.referrer || '';
  var now = Date.now();
  var state = readLS();
  var isNewSession = false;

  if (!state) {
    state = { created: now, first: null, last: null, visits: [], sid: uuid(), uid: uuid() };
  }
  if (!state.sid) state.sid = uuid();
  if (!state.uid) {
    state.uid = readCookie(COOKIE) || uuid();
    writeCookie(COOKIE, state.uid, 400);
  } else {
    writeCookie(COOKIE, state.uid, 400);
  }

  var hasUtm = Object.keys(p).some(function (k) { return k.indexOf('utm_') === 0 || k === 'epik' || k === 'gclid' || k === 'view'; });
  // session clock runs on EVERY pageview (even untagged ones), attribution below does not
  var prevVisitTs = state.lastVisitTs || (state.last && state.last.ts) || 0;
  var sinceLast = prevVisitTs ? now - prevVisitTs : Infinity;
  isNewSession = sinceLast > SESSION_MS || (hasUtm && ref && host(ref) !== host(location.href));
  state.lastVisitTs = now;

  var visit = {
    ts: now,
    path: location.pathname + location.search,
    ref: ref,
    ref_host: host(ref),
    landing: location.origin + location.pathname + location.search,
    params: Object.keys(p).length ? p : {}
  };
  var cls = classify(ref, p);
  visit.source = cls.source; visit.medium = cls.medium; visit.campaign = cls.campaign;

  if (isNewSession) state.sid = uuid();

  // First touch: only ever written once (unless the stored record expired).
  if (!state.first) {
    state.first = {
      ts: now,
      source: cls.source, medium: cls.medium, campaign: cls.campaign, content: cls.content || '',
      landing: visit.landing, ref: ref, ref_host: host(ref), params: visit.params
    };
  }

  // Last ATTRIBUTED touch: only overwritten by a visit we can actually attribute
  // (referral / platform signature / UTM) — a bare direct visit never clobbers it,
  // while state.lastVisitTs above still keeps the session clock honest.
  var isTagged = !!(cls.source && cls.source !== '(direct)');
  if (isTagged || !state.last) {
    state.last = {
      ts: now, source: cls.source, medium: cls.medium, campaign: cls.campaign,
      landing: visit.landing, ref: ref, ref_host: host(ref), params: visit.params
    };
  }

  state.visits.push(visit);
  if (state.visits.length > MAX_VISITS) state.visits = state.visits.slice(-MAX_VISITS);
  writeLS(state);

  /* ---------- copy the journey onto the cart so the ORDER carries it ---------- */
  function compactTouch(t) {
    if (!t) return null;
    return { ts: t.ts, src: t.source, med: t.medium, camp: t.campaign || '', land: t.landing, ref: t.ref_host || host(t.ref) };
  }
  function attrs() {
    var trail = state.visits.slice(-12).map(function (v) {
      return { t: new Date(v.ts).toISOString().slice(0, 16), s: v.source, m: v.medium, p: v.path.slice(0, 90) };
    });
    return {
      phn_uid: state.uid,
      phn_sid: state.sid,
      phn_first: JSON.stringify(compactTouch(state.first)),
      phn_last: JSON.stringify(compactTouch(state.last)),
      phn_trail: JSON.stringify(trail),
      phn_visits: String(state.visits.length)
    };
  }

  var busy = false, appliedSig = '';
  function currentSig() {
    return (state.first ? state.first.ts : 0) + '|' + (state.last ? state.last.ts : 0) + '|' + state.visits.length;
  }
  function apply() {
    if (busy) return;
    var sig = currentSig();
    if (sig === appliedSig) return;      // nothing new since last successful write
    busy = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) { busy = false; return; }   // never create empty carts
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs() })
        }).then(function () { appliedSig = sig; })
          .catch(function () {})
          .then(function () { busy = false; });
      })
      .catch(function () { busy = false; });
  }

  // run on load (only when there is something in the cart) and after cart mutations
  setTimeout(apply, 800);
  var reapply = function () { setTimeout(apply, 400); };

  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = (arguments[0] && (typeof arguments[0] === 'string' ? arguments[0] : arguments[0].url)) || '';
      var ret = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        ret.then(reapply).catch(function () {});
      }
      return ret;
    };
  }

  // SPA-ish navigations (turbolinks/theme transitions) keep the record fresh
  window.addEventListener('pageshow', function (e) { if (e.persisted) setTimeout(apply, 600); });

  window.__phn = state;
})();
